import * as THREE from 'three';
import type { Lighting, SceneManifest, SceneTier } from '@cfg/shared';
import { bindingUniforms, hexToLinear, type MaterialBinding } from '../materials';
import { largestSceneTier, neededSceneWidth, pickSceneTier, type TierLimits } from '../tiers';
import { bitmapPixels, dataTexture, loadBitmap, TextureCache } from './loader';
import { DISPLAY_FRAG, FULLSCREEN_VERT, MAX_ZONES, SCENE_FRAG } from './shaders';

export interface SceneSource {
  /** Base URL of the scene package (manifest paths are relative to it). */
  baseUrl: string;
  manifest: SceneManifest;
}

export type CompositorEvent =
  | { type: 'loading'; sceneKey: string; lighting: Lighting }
  | { type: 'ready'; sceneKey: string; tier: SceneTier; ms: number }
  /** A sharper tier replaced the displayed one (zoom, resize, full screen or pixel ratio). */
  | { type: 'tier'; sceneKey: string; tier: SceneTier; ms: number }
  | { type: 'rendered'; ms: number }
  /** The WebGL context came back after a loss; the GPU resources were re-created and redrawn. */
  | { type: 'restored' }
  | { type: 'error'; error: Error };

/**
 * Reported as an `error` event when the browser drops the WebGL context (GPU reset, driver
 * update, too many contexts). The compositor redraws by itself once the context is restored.
 */
export class ContextLostError extends Error {
  constructor() {
    super('WebGL context lost');
    this.name = 'ContextLostError';
  }
}

export interface CompositorOptions {
  canvas: HTMLCanvasElement;
  /** Upper bound for devicePixelRatio (default 2). */
  maxPixelRatio?: number;
  /** GPU budget for material textures in MB (default 384, 192 on small screens). */
  textureBudgetMb?: number;
  /** Letterbox colour (CSS hex). */
  background?: string;
  fit?: 'contain' | 'cover';
  /** Largest scene tier to load (e.g. 1280 on mobile). */
  maxTierWidth?: number;
  onEvent?: (event: CompositorEvent) => void;
}

export interface ExportImageOptions {
  /**
   * Caps the width of the exported image. By default the largest tier allowed by
   * `maxTierWidth` (and the GPU texture limit) is used, whatever tier is on screen.
   */
  maxWidth?: number;
}

export interface ViewRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface LightingAssets {
  bg: THREE.Texture;
  diffuse: THREE.Texture;
  gloss: THREE.Texture;
  satin: THREE.Texture;
  scale: THREE.Texture;
  exposure: number;
}

const FULL_VIEW: ViewRect = { x0: 0, y0: 0, x1: 1, y1: 1 };

function srgbHexToDisplay(hex: string): THREE.Vector3 {
  const n = parseInt(hex.slice(1), 16);
  return new THREE.Vector3(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
}

function idle(callback: () => void): void {
  if ('requestIdleCallback' in globalThis) {
    (globalThis as unknown as { requestIdleCallback: (f: () => void) => void }).requestIdleCallback(
      callback,
    );
  } else {
    setTimeout(callback, 300);
  }
}

/** Disposes a texture and closes its decoded image. */
function releaseTexture(texture: THREE.Texture): void {
  (texture.image as ImageBitmap | undefined)?.close?.();
  texture.dispose();
}

function releaseLighting(assets: LightingAssets): void {
  for (const t of [assets.bg, assets.diffuse, assets.gloss, assets.satin, assets.scale]) {
    releaseTexture(t);
  }
}

/** Loads several bitmaps; if one fails, the others are closed before the error is rethrown. */
async function loadBitmaps(urls: string[]): Promise<ImageBitmap[]> {
  const results = await Promise.allSettled(urls.map((url) => loadBitmap(url, 'data')));
  const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failed) {
    for (const r of results) if (r.status === 'fulfilled') r.value.close();
    throw failed.reason;
  }
  return results.map((r) => (r as PromiseFulfilledResult<ImageBitmap>).value);
}

/** The passes a tier needs besides the lightings. */
interface TierSurface {
  texture: THREE.Texture;
  pixels: Uint8ClampedArray;
}

/**
 * Real-time compositor for pre-rendered multi-pass scenes. It re-textures swappable surfaces in
 * a fragment shader using per-surface homographies and the HDR lighting passes, renders each
 * lighting into its own render target (only when something changes) and displays them with
 * zoom/pan and an animated day/night crossfade.
 *
 * The scene tier follows the pixel density the scene is shown at: zooming in, enlarging the
 * canvas (full screen) or moving to a denser screen loads a sharper tier in the background and
 * switches to it once its passes are ready. It never switches to a smaller tier until the next
 * `setScene`.
 */
export class SceneCompositor {
  readonly renderer: THREE.WebGLRenderer;
  protected readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  protected readonly quad: THREE.Mesh;
  protected readonly quadScene = new THREE.Scene();
  protected sceneMaterial: THREE.RawShaderMaterial;
  protected displayMaterial: THREE.RawShaderMaterial;
  protected readonly textures: TextureCache;
  protected readonly options: Required<Omit<CompositorOptions, 'onEvent' | 'maxTierWidth'>> &
    Pick<CompositorOptions, 'onEvent' | 'maxTierWidth'>;

  protected source: SceneSource | null = null;
  protected tier: SceneTier | null = null;
  protected assets = new Map<Lighting, LightingAssets>();
  protected assetLoads = new Map<Lighting, Promise<LightingAssets>>();
  protected surfaceTexture: THREE.Texture | null = null;
  protected surfaceData: THREE.DataTexture | null = null;
  protected surfacePixels: Uint8ClampedArray | null = null;
  protected targets = new Map<Lighting, THREE.WebGLRenderTarget>();
  protected dirty = new Set<Lighting>();
  protected bindings = new Map<string, MaterialBinding | null>();
  protected pendingBindings = new Map<string, MaterialBinding | null>();
  protected boundTextures = new Map<string, THREE.Texture | null>();
  protected placeholder: THREE.DataTexture;
  protected loadToken = 0;
  /** Changes whenever the displayed tier changes (scene load or upgrade). */
  protected tierGeneration = 0;
  /** Tier being loaded to replace the displayed one. */
  protected upgrade: { tier: SceneTier } | null = null;
  protected contextLost = false;

  protected lighting: Lighting = 'day';
  protected fromLighting: Lighting = 'day';
  protected mix = 1;
  protected mixStart = 0;
  protected mixDuration = 650;
  protected highlightZone: string | null = null;
  protected highlight = 0;
  protected view: ViewRect = { ...FULL_VIEW };
  protected frameRequested = false;
  protected disposed = false;
  private resizeObserver: ResizeObserver | null = null;
  private stopWatchingPixelRatio: (() => void) | null = null;
  /** Tiers that failed to load for the current scene (not retried until the next scene). */
  private failedTiers = new Set<string>();

  constructor(options: CompositorOptions) {
    const small =
      typeof window !== 'undefined' && Math.min(window.screen.width, window.screen.height) < 700;
    this.options = {
      canvas: options.canvas,
      maxPixelRatio: options.maxPixelRatio ?? 2,
      textureBudgetMb: options.textureBudgetMb ?? (small ? 192 : 384),
      background: options.background ?? '#f3f1ed',
      fit: options.fit ?? 'contain',
      maxTierWidth: options.maxTierWidth,
      onEvent: options.onEvent,
    };
    this.renderer = new THREE.WebGLRenderer({
      canvas: options.canvas,
      antialias: false,
      alpha: false,
      depth: false,
      stencil: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.autoClear = false;
    const anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.textures = new TextureCache(this.options.textureBudgetMb * 1024 * 1024, () =>
      Math.min(8, anisotropy),
    );
    this.placeholder = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    this.placeholder.needsUpdate = true;

    this.sceneMaterial = this.createSceneMaterial();
    this.displayMaterial = this.createDisplayMaterial();
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.sceneMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    // registered after three.js's own handlers, so its state is re-initialised first
    options.canvas.addEventListener('webglcontextlost', this.onContextLost);
    options.canvas.addEventListener('webglcontextrestored', this.onContextRestored);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(options.canvas);
    }
    this.watchPixelRatio();
    this.resize();
  }

  protected createSceneMaterial(): THREE.RawShaderMaterial {
    return new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: SCENE_FRAG,
      uniforms: this.baseUniforms(),
      depthTest: false,
      depthWrite: false,
    });
  }

  protected createDisplayMaterial(): THREE.RawShaderMaterial {
    return new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FULLSCREEN_VERT,
      fragmentShader: DISPLAY_FRAG,
      uniforms: {
        uA: { value: null },
        uB: { value: null },
        uMix: { value: 0 },
        uView: { value: new THREE.Vector4(0, 0, 1, 1) },
        uCanvas: { value: new THREE.Vector2(1, 1) },
        uScene: { value: new THREE.Vector2(16, 9) },
        uBackground: { value: srgbHexToDisplay(this.options.background) },
        uFit: { value: this.options.fit === 'cover' ? 1 : 0 },
      },
      depthTest: false,
      depthWrite: false,
    });
  }

  protected baseUniforms(): Record<string, THREE.IUniform> {
    const uniforms: Record<string, THREE.IUniform> = {
      uBg: { value: null },
      uDiff: { value: null },
      uGloss: { value: null },
      uSatin: { value: null },
      uScale: { value: null },
      uSurf: { value: null },
      uSurfData: { value: null },
      uRes: { value: new THREE.Vector2(1, 1) },
      uExposure: { value: 1 },
      uGamma: { value: 2.2 },
      uScaleMin: { value: -10 },
      uScaleMax: { value: 7 },
      uHighlightZone: { value: -1 },
      uHighlight: { value: 0 },
      uMatA: { value: Array.from({ length: MAX_ZONES }, () => new THREE.Vector4(1, 1, 1, 0)) },
      uMatB: { value: Array.from({ length: MAX_ZONES }, () => new THREE.Vector4()) },
      uMatC: { value: Array.from({ length: MAX_ZONES }, () => new THREE.Vector4()) },
    };
    for (let i = 0; i < MAX_ZONES; i++) uniforms[`uMat${i}`] = { value: this.placeholder };
    return uniforms;
  }

  // ------------------------------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------------------------------

  get currentScene(): SceneSource | null {
    return this.source;
  }

  get currentTier(): SceneTier | null {
    return this.tier;
  }

  get zones(): string[] {
    return this.source?.manifest.zones ?? [];
  }

  /** Loads a scene package; resolves once the current lighting can be displayed. */
  async setScene(source: SceneSource, lighting: Lighting = this.lighting): Promise<void> {
    const token = ++this.loadToken;
    const started = performance.now();
    this.upgrade = null;
    this.failedTiers.clear();
    this.releaseScene();
    this.source = source;
    this.lighting = lighting;
    this.fromLighting = lighting;
    this.mix = 1;
    this.view = { ...FULL_VIEW };
    const tier = this.chooseTier(source.manifest);
    this.tier = tier;
    this.tierGeneration++;
    this.options.onEvent?.({ type: 'loading', sceneKey: source.manifest.key, lighting });

    const surfaceBitmap = loadBitmap(this.url(source.manifest.surfaceMap), 'data');
    const assets = this.loadLighting(lighting);
    const [bitmap] = await Promise.all([surfaceBitmap, assets]);
    if (token !== this.loadToken) {
      bitmap.close();
      return;
    }
    this.surfaceTexture = dataTexture(bitmap, 'nearest');
    this.surfacePixels = bitmapPixels(bitmap);
    this.surfaceData = this.buildSurfaceData(source.manifest);
    for (const zone of source.manifest.zones) {
      if (!this.bindings.has(zone)) this.bindings.set(zone, null);
    }
    this.markAllDirty();
    this.requestFrame();
    this.options.onEvent?.({
      type: 'ready',
      sceneKey: source.manifest.key,
      tier,
      ms: performance.now() - started,
    });
    // Preload the other lighting in the background.
    const other: Lighting = lighting === 'day' ? 'night' : 'day';
    if (source.manifest.lightings[other]) {
      idle(() => {
        if (token === this.loadToken) this.loadLighting(other).catch(() => undefined);
      });
    }
    // the canvas may have grown while the scene was loading
    this.checkTier();
  }

  /** Switches lighting with a crossfade (loads the passes first if needed). */
  async setLighting(lighting: Lighting, animate = true): Promise<void> {
    if (!this.source?.manifest.lightings[lighting]) return;
    if (lighting === this.lighting && this.mix >= 1) return;
    const token = this.loadToken;
    // loop: the tier may change while the passes load
    while (!this.assets.has(lighting)) {
      await this.loadLighting(lighting);
      if (token !== this.loadToken) return;
    }
    this.fromLighting = this.mix >= 1 ? this.lighting : this.fromLighting;
    this.lighting = lighting;
    this.dirty.add(lighting);
    if (animate && this.fromLighting !== lighting) {
      this.mix = 0;
      this.mixStart = performance.now();
    } else {
      this.mix = 1;
    }
    this.requestFrame();
  }

  get currentLighting(): Lighting {
    return this.lighting;
  }

  /**
   * Applies (or clears) the material of a zone. The previous material stays visible until the
   * new texture is on the GPU, so swaps never flash. Resolves when the swap is visible.
   */
  async setMaterial(zone: string, binding: MaterialBinding | null): Promise<void> {
    const previous = this.pendingBindings.has(zone)
      ? this.pendingBindings.get(zone)
      : this.bindings.get(zone);
    if (previous?.key === binding?.key && previous?.textureUrl === binding?.textureUrl) return;
    this.pendingBindings.set(zone, binding);
    let texture: THREE.Texture | null = null;
    if (binding?.textureUrl) {
      try {
        texture = await this.textures.get(binding.textureUrl, this.pinnedUrls());
      } catch (error) {
        if (this.pendingBindings.get(zone) === binding) this.pendingBindings.delete(zone);
        this.options.onEvent?.({ type: 'error', error: error as Error });
        return;
      }
    }
    if (this.pendingBindings.get(zone) !== binding) return;
    this.pendingBindings.delete(zone);
    this.bindings.set(zone, binding);
    this.boundTextures.set(zone, texture);
    this.markAllDirty();
    this.requestFrame();
  }

  async setMaterials(bindings: Record<string, MaterialBinding | null>): Promise<void> {
    await Promise.all(Object.entries(bindings).map(([zone, b]) => this.setMaterial(zone, b)));
  }

  /** Warms the texture cache (e.g. on hover) without applying the material. */
  preload(url: string): void {
    void this.textures.get(url, this.pinnedUrls()).catch(() => undefined);
  }

  setHighlight(zone: string | null): void {
    if (zone === this.highlightZone) return;
    this.highlightZone = zone;
    this.highlight = zone ? 1 : 0;
    this.markAllDirty();
    this.requestFrame();
  }

  /** Zone under a client-space point, or null. */
  pick(clientX: number, clientY: number): string | null {
    const uv = this.clientToScene(clientX, clientY);
    if (!uv || !this.surfacePixels || !this.tier || !this.source) return null;
    const x = Math.min(this.tier.width - 1, Math.floor(uv[0] * this.tier.width));
    const y = Math.min(this.tier.height - 1, Math.floor(uv[1] * this.tier.height));
    const i = (y * this.tier.width + x) * 4;
    const a = this.surfacePixels[i]!;
    const b = this.surfacePixels[i + 1]!;
    const wA = this.surfacePixels[i + 2]! / 255;
    const id = wA >= 0.5 ? a : b;
    if (id === 0) return null;
    return this.source.manifest.surfaces.find((s) => s.id === id)?.zone ?? null;
  }

  /** Normalised scene coordinates (x right, y down) for a client-space point. */
  clientToScene(clientX: number, clientY: number): [number, number] | null {
    const rect = this.options.canvas.getBoundingClientRect();
    if (!this.tier || rect.width === 0) return null;
    const cw = rect.width;
    const ch = rect.height;
    const sw = this.tier.width;
    const sh = this.tier.height;
    const scale =
      this.options.fit === 'cover' ? Math.max(cw / sw, ch / sh) : Math.min(cw / sw, ch / sh);
    const w = sw * scale;
    const h = sh * scale;
    const st = [(clientX - rect.left - (cw - w) / 2) / w, (clientY - rect.top - (ch - h) / 2) / h];
    if (st[0]! < 0 || st[0]! > 1 || st[1]! < 0 || st[1]! > 1) return null;
    const v = this.view;
    return [v.x0 + st[0]! * (v.x1 - v.x0), v.y0 + st[1]! * (v.y1 - v.y0)];
  }

  get zoom(): number {
    return 1 / (this.view.x1 - this.view.x0);
  }

  /** Zooms around a client-space point; factor > 1 zooms in. */
  zoomAt(clientX: number, clientY: number, factor: number, maxZoom = 4): void {
    const anchor = this.clientToScene(clientX, clientY) ?? [0.5, 0.5];
    const current = this.view.x1 - this.view.x0;
    const next = Math.min(1, Math.max(1 / maxZoom, current / factor));
    const k = next / current;
    const v = this.view;
    this.setView({
      x0: anchor[0] - (anchor[0] - v.x0) * k,
      y0: anchor[1] - (anchor[1] - v.y0) * k,
      x1: anchor[0] + (v.x1 - anchor[0]) * k,
      y1: anchor[1] + (v.y1 - anchor[1]) * k,
    });
  }

  /** Pans by a client-space delta (pixels). */
  panBy(dxPixels: number, dyPixels: number): void {
    const rect = this.options.canvas.getBoundingClientRect();
    const v = this.view;
    const dx = (dxPixels / rect.width) * (v.x1 - v.x0);
    const dy = (dyPixels / rect.height) * (v.y1 - v.y0);
    this.setView({ x0: v.x0 - dx, y0: v.y0 - dy, x1: v.x1 - dx, y1: v.y1 - dy });
  }

  /** Sets the visible region (normalised). The region is kept square in normalised units so
   *  the image is never distorted; its size is taken from the width. */
  setView(view: ViewRect): void {
    const w = Math.min(1, Math.max(0.05, view.x1 - view.x0));
    const h = w;
    const x0 = Math.min(1 - w, Math.max(0, view.x0));
    const y0 = Math.min(1 - h, Math.max(0, view.y0));
    this.view = { x0, y0, x1: x0 + w, y1: y0 + h };
    this.requestFrame();
    this.checkTier();
  }

  resetView(): void {
    this.setView({ ...FULL_VIEW });
  }

  resize(): void {
    const canvas = this.options.canvas;
    const dpr = this.pixelRatio();
    const width = Math.max(1, Math.round(canvas.clientWidth * dpr));
    const height = Math.max(1, Math.round(canvas.clientHeight * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      this.renderer.setSize(width, height, false);
    }
    this.requestFrame();
    this.checkTier();
  }

  /**
   * Renders the current composite and returns it as an image blob. The image is rendered
   * off-screen at the largest allowed tier (loaded for the occasion if it is not the one on
   * screen, then released); if that tier cannot be loaded, the displayed tier is used.
   */
  async exportImage(
    type = 'image/jpeg',
    quality = 0.92,
    options: ExportImageOptions = {},
  ): Promise<Blob> {
    if (this.contextLost) throw new ContextLostError();
    const source = this.source;
    if (!source || !this.tier || !this.surfaceTexture || !this.assets.has(this.lighting)) {
      throw new Error('Scene not ready');
    }
    const token = this.loadToken;
    const lighting = this.lighting;
    const wanted = largestSceneTier(source.manifest.tiers, this.tierLimits(), options.maxWidth);

    let temporary: { tier: SceneTier; surface: TierSurface; assets: LightingAssets } | null = null;
    if (wanted.width !== this.tier.width) {
      const [surface, assets] = await Promise.allSettled([
        this.fetchSurface(source, wanted),
        this.fetchLighting(source, wanted, lighting),
      ]);
      if (surface.status === 'fulfilled' && assets.status === 'fulfilled') {
        temporary = { tier: wanted, surface: surface.value, assets: assets.value };
      } else {
        // fall back to the tier on screen
        if (surface.status === 'fulfilled') releaseTexture(surface.value.texture);
        if (assets.status === 'fulfilled') releaseLighting(assets.value);
      }
    }
    if (token !== this.loadToken || this.disposed || this.contextLost) {
      if (temporary) this.releaseTemporary(temporary);
      if (this.contextLost) throw new ContextLostError();
      throw new Error('Scene changed while exporting');
    }

    // (without a temporary tier, re-read the displayed state: it may have changed meanwhile)
    const tier = temporary?.tier ?? this.tier;
    const surface = temporary?.surface.texture ?? this.surfaceTexture;
    const assets = temporary?.assets ?? this.assets.get(this.lighting);
    if (!tier || !surface || !assets) throw new Error('Scene not ready');

    let pixels: Uint8Array;
    try {
      pixels = this.renderOffscreen(tier, surface, assets);
    } finally {
      if (temporary) this.releaseTemporary(temporary);
    }

    const canvas = document.createElement('canvas');
    canvas.width = tier.width;
    canvas.height = tier.height;
    const ctx = canvas.getContext('2d')!;
    const image = ctx.createImageData(tier.width, tier.height);
    // render targets are stored bottom-up
    for (let y = 0; y < tier.height; y++) {
      const src = (tier.height - 1 - y) * tier.width * 4;
      image.data.set(pixels.subarray(src, src + tier.width * 4), y * tier.width * 4);
    }
    ctx.putImageData(image, 0, 0);
    return new Promise((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Export failed'))),
        type,
        quality,
      ),
    );
  }

  dispose(): void {
    this.disposed = true;
    this.loadToken++;
    this.upgrade = null;
    this.resizeObserver?.disconnect();
    this.stopWatchingPixelRatio?.();
    this.options.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.options.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.releaseScene();
    this.textures.dispose();
    this.placeholder.dispose();
    this.sceneMaterial.dispose();
    this.displayMaterial.dispose();
    this.quad.geometry.dispose();
    this.renderer.dispose();
  }

  // ------------------------------------------------------------------------------------------
  // Internals
  // ------------------------------------------------------------------------------------------

  protected url(file: string, tier: SceneTier = this.tier!): string {
    return this.tierUrl(this.source!, tier, file);
  }

  protected tierUrl(source: SceneSource, tier: SceneTier, file: string): string {
    return `${source.baseUrl.replace(/\/$/, '')}/${tier.dir}/${file}`;
  }

  protected pixelRatio(): number {
    return Math.min(this.options.maxPixelRatio, globalThis.devicePixelRatio || 1);
  }

  protected tierLimits(): TierLimits {
    return {
      maxTierWidth: this.options.maxTierWidth,
      maxTextureSize: this.renderer.capabilities.maxTextureSize,
    };
  }

  /** Tier width needed to show the scene sharply at the current size, pixel ratio and zoom. */
  protected neededWidth(manifest: SceneManifest): number {
    const canvas = this.options.canvas;
    const largest = manifest.tiers.reduce((a, b) => (b.width > a.width ? b : a));
    return neededSceneWidth({
      canvasWidth: canvas.clientWidth,
      canvasHeight: canvas.clientHeight,
      sceneAspect: largest.width / largest.height,
      fit: this.options.fit,
      pixelRatio: this.pixelRatio(),
      zoom: this.zoom,
    });
  }

  protected chooseTier(manifest: SceneManifest): SceneTier {
    return pickSceneTier(manifest.tiers, this.neededWidth(manifest), this.tierLimits());
  }

  /** Starts loading a sharper tier when the scene is shown larger than the current tier. */
  protected checkTier(): void {
    const source = this.source;
    const tier = this.tier;
    if (!source || !tier || this.disposed || this.contextLost) return;
    // wait until the scene is on screen (setScene checks again when it is)
    if (!this.surfaceTexture || !this.assets.has(this.lighting)) return;
    const next = this.chooseTier(source.manifest);
    // never downgrade; an upgrade to at least this size may already be loading
    if (next.width <= Math.max(tier.width, this.upgrade?.tier.width ?? 0)) return;
    if (this.failedTiers.has(next.dir)) return;
    void this.upgradeTier(source, next);
  }

  /**
   * Loads `next` and swaps it in once the visible lighting(s) are ready. The displayed tier,
   * materials and view stay untouched until then, so the switch never flashes.
   */
  protected async upgradeTier(source: SceneSource, next: SceneTier): Promise<void> {
    const job = { tier: next };
    this.upgrade = job;
    const token = this.loadToken;
    const started = performance.now();
    const loaded = new Map<Lighting, LightingAssets>();
    const got: { surface: TierSurface | null } = { surface: null };
    let abandoned = false;
    const stale = () =>
      abandoned || this.disposed || token !== this.loadToken || this.upgrade !== job;
    const abandon = () => {
      abandoned = true;
      for (const assets of loaded.values()) releaseLighting(assets);
      loaded.clear();
      if (got.surface) releaseTexture(got.surface.texture);
      got.surface = null;
    };
    // results that arrive after the upgrade was abandoned are released right away
    const fetchInto = (lighting: Lighting) =>
      this.fetchLighting(source, next, lighting).then((assets) => {
        if (abandoned) releaseLighting(assets);
        else loaded.set(lighting, assets);
      });
    // lightings on screen when the switch happens (the visitor may switch meanwhile)
    const missing = () => {
      const visible = new Set<Lighting>([this.lighting]);
      if (this.mix < 1) visible.add(this.fromLighting);
      return [...visible].filter((l) => !loaded.has(l));
    };

    try {
      await Promise.all([
        this.fetchSurface(source, next).then((s) => {
          if (abandoned) releaseTexture(s.texture);
          else got.surface = s;
        }),
        ...missing().map(fetchInto),
      ]);
      for (let todo = missing(); !stale() && todo.length; todo = missing()) {
        await Promise.all(todo.map(fetchInto));
      }
    } catch {
      const current = !stale();
      abandon();
      if (current) {
        // keep showing the current tier; do not retry this one for the scene
        this.failedTiers.add(next.dir);
        this.upgrade = null;
      }
      return;
    }
    const ready = got.surface;
    if (stale() || !ready || this.contextLost) {
      if (this.upgrade === job) this.upgrade = null;
      abandon();
      return;
    }

    // swap: everything below runs synchronously, before the next frame
    this.upgrade = null;
    const previouslyLoaded = [...this.assets.keys()].filter((l) => !loaded.has(l));
    this.releaseTier();
    this.tier = next;
    this.tierGeneration++;
    this.surfaceTexture = ready.texture;
    this.surfacePixels = ready.pixels;
    for (const [lighting, assets] of loaded) {
      this.assets.set(lighting, assets);
      this.assetLoads.set(lighting, Promise.resolve(assets));
    }
    this.onTierChanged();
    this.markAllDirty();
    this.requestFrame();
    this.options.onEvent?.({
      type: 'tier',
      sceneKey: source.manifest.key,
      tier: next,
      ms: performance.now() - started,
    });
    // reload the other lighting(s) at the new tier so day/night switches stay instant
    if (previouslyLoaded.length) {
      idle(() => {
        if (token !== this.loadToken) return;
        for (const l of previouslyLoaded) this.loadLighting(l).catch(() => undefined);
      });
    }
    // the view may have grown further while this tier was loading
    this.checkTier();
  }

  /** Called whenever `tier` changes outside `setScene` (upgrade, off-screen export). */
  protected onTierChanged(): void {}

  protected loadLighting(lighting: Lighting): Promise<LightingAssets> {
    const existing = this.assetLoads.get(lighting);
    if (existing) return existing;
    const source = this.source;
    const tier = this.tier;
    if (!source || !tier) return Promise.reject(new Error('No scene'));
    const token = this.loadToken;
    const generation = this.tierGeneration;
    const promise: Promise<LightingAssets> = this.fetchLighting(source, tier, lighting).then(
      (assets) => {
        if (token !== this.loadToken) {
          releaseLighting(assets);
          throw new Error('Scene changed while loading');
        }
        if (generation !== this.tierGeneration) {
          // a sharper tier replaced this one meanwhile: load its passes instead
          releaseLighting(assets);
          return this.loadLighting(lighting);
        }
        this.assets.set(lighting, assets);
        this.dirty.add(lighting);
        return assets;
      },
    );
    promise.catch((error: unknown) => {
      if (this.assetLoads.get(lighting) === promise) this.assetLoads.delete(lighting);
      if (token === this.loadToken && generation === this.tierGeneration) {
        this.options.onEvent?.({ type: 'error', error: error as Error });
      }
    });
    this.assetLoads.set(lighting, promise);
    return promise;
  }

  /** Loads the passes of one lighting at a given tier (not stored). */
  protected async fetchLighting(
    source: SceneSource,
    tier: SceneTier,
    lighting: Lighting,
  ): Promise<LightingAssets> {
    const files = source.manifest.lightings[lighting];
    if (!files) throw new Error(`Scene has no ${lighting} lighting`);
    const [bg, diffuse, gloss, satin, scale] = await loadBitmaps(
      [files.background, files.diffuse, files.reflGloss, files.reflSatin, files.scale].map((f) =>
        this.tierUrl(source, tier, f),
      ),
    );
    return {
      bg: dataTexture(bg!, 'nearest'),
      diffuse: dataTexture(diffuse!, 'nearest'),
      gloss: dataTexture(gloss!, 'nearest'),
      satin: dataTexture(satin!, 'nearest'),
      scale: dataTexture(scale!, 'linear'),
      exposure: files.exposure,
    };
  }

  /** Loads the surface-id map of a tier (not stored). */
  protected async fetchSurface(source: SceneSource, tier: SceneTier): Promise<TierSurface> {
    const bitmap = await loadBitmap(this.tierUrl(source, tier, source.manifest.surfaceMap), 'data');
    return { texture: dataTexture(bitmap, 'nearest'), pixels: bitmapPixels(bitmap) };
  }

  private releaseTemporary(temporary: { surface: TierSurface; assets: LightingAssets }): void {
    releaseTexture(temporary.surface.texture);
    releaseLighting(temporary.assets);
  }

  /**
   * Renders the scene composite of `assets` at `tier` into a temporary target and reads it
   * back (bottom-up RGBA). The displayed tier is restored afterwards.
   */
  protected renderOffscreen(
    tier: SceneTier,
    surface: THREE.Texture,
    assets: LightingAssets,
  ): Uint8Array {
    const saved = {
      tier: this.tier,
      surface: this.surfaceTexture,
      highlightZone: this.highlightZone,
      highlight: this.highlight,
    };
    const target = new THREE.WebGLRenderTarget(tier.width, tier.height, {
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    const swapTier = saved.tier?.width !== tier.width;
    try {
      this.tier = tier;
      this.surfaceTexture = surface;
      // exports never show the hover highlight
      this.highlightZone = null;
      this.highlight = 0;
      if (swapTier) this.onTierChanged();
      this.applyUniforms(this.sceneMaterial, assets);
      this.quad.material = this.sceneMaterial;
      this.renderer.setRenderTarget(target);
      this.renderer.render(this.quadScene, this.camera);
      const pixels = new Uint8Array(tier.width * tier.height * 4);
      this.renderer.readRenderTargetPixels(target, 0, 0, tier.width, tier.height, pixels);
      return pixels;
    } finally {
      this.renderer.setRenderTarget(null);
      target.dispose();
      this.tier = saved.tier;
      this.surfaceTexture = saved.surface;
      this.highlightZone = saved.highlightZone;
      this.highlight = saved.highlight;
      // (the scene material still points at the export textures; every draw re-applies them)
      if (swapTier) this.onTierChanged();
    }
  }

  protected buildSurfaceData(manifest: SceneManifest): THREE.DataTexture {
    const width = 256;
    const data = new Float32Array(width * 3 * 4);
    for (const surface of manifest.surfaces) {
      const zoneIndex = surface.zone ? manifest.zones.indexOf(surface.zone) : -1;
      const h = surface.h;
      const row = (r: number, values: number[]) => data.set(values, (r * width + surface.id) * 4);
      row(0, [h[0]!, h[1]!, h[2]!, zoneIndex + 1]);
      row(1, [h[3]!, h[4]!, h[5]!, surface.uvOffset[0]]);
      row(2, [h[6]!, h[7]!, h[8]!, surface.uvOffset[1]]);
    }
    const texture = new THREE.DataTexture(data, width, 3, THREE.RGBAFormat, THREE.FloatType);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    return texture;
  }

  protected pinnedUrls(): Set<string> {
    const set = new Set<string>();
    for (const b of this.bindings.values()) if (b?.textureUrl) set.add(b.textureUrl);
    for (const b of this.pendingBindings.values()) if (b?.textureUrl) set.add(b.textureUrl);
    return set;
  }

  protected markAllDirty(): void {
    for (const l of this.assets.keys()) this.dirty.add(l);
  }

  protected target(lighting: Lighting): THREE.WebGLRenderTarget {
    const tier = this.tier!;
    let rt = this.targets.get(lighting);
    if (!rt || rt.width !== tier.width || rt.height !== tier.height) {
      rt?.dispose();
      rt = new THREE.WebGLRenderTarget(tier.width, tier.height, {
        type: THREE.UnsignedByteType,
        format: THREE.RGBAFormat,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
        magFilter: THREE.LinearFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
      this.targets.set(lighting, rt);
    }
    return rt;
  }

  /** Sets scene uniforms common to all compositors. Subclasses extend this. */
  protected applyUniforms(material: THREE.RawShaderMaterial, assets: LightingAssets): void {
    const u = material.uniforms as Record<string, THREE.IUniform>;
    const manifest = this.source!.manifest;
    const tier = this.tier!;
    u.uBg!.value = assets.bg;
    u.uDiff!.value = assets.diffuse;
    u.uGloss!.value = assets.gloss;
    u.uSatin!.value = assets.satin;
    u.uScale!.value = assets.scale;
    u.uSurf!.value = this.surfaceTexture;
    u.uSurfData!.value = this.surfaceData;
    (u.uRes!.value as THREE.Vector2).set(tier.width, tier.height);
    u.uExposure!.value = assets.exposure;
    u.uGamma!.value = manifest.hdr.gamma;
    u.uScaleMin!.value = manifest.hdr.scaleMin;
    u.uScaleMax!.value = manifest.hdr.scaleMax;
    u.uHighlightZone!.value = this.highlightZone ? manifest.zones.indexOf(this.highlightZone) : -1;
    u.uHighlight!.value = this.highlight;
    manifest.zones.forEach((zone, i) => {
      if (i >= MAX_ZONES) return;
      const binding = this.bindings.get(zone) ?? null;
      const a = (u.uMatA!.value as THREE.Vector4[])[i]!;
      const b = (u.uMatB!.value as THREE.Vector4[])[i]!;
      const c = (u.uMatC!.value as THREE.Vector4[])[i]!;
      if (!binding) {
        c.set(0, 0, 0, 0);
        u[`uMat${i}`]!.value = this.placeholder;
        return;
      }
      const values = bindingUniforms(binding);
      a.set(...values.a);
      b.set(...values.b);
      const texture = this.boundTextures.get(zone) ?? null;
      if (binding.textureUrl && !texture) {
        // texture evicted or unavailable: fall back to the average tone
        b.w = 0;
        const avg = hexToLinear(binding.color ?? '#9a9a9a');
        c.set(avg[0], avg[1], avg[2], 1);
      } else {
        c.set(...values.c);
      }
      u[`uMat${i}`]!.value = texture ?? this.placeholder;
    });
  }

  protected renderLighting(lighting: Lighting): void {
    const assets = this.assets.get(lighting);
    if (!assets || !this.surfaceTexture || this.contextLost) return;
    const started = performance.now();
    this.applyUniforms(this.sceneMaterial, assets);
    this.quad.material = this.sceneMaterial;
    this.renderer.setRenderTarget(this.target(lighting));
    this.renderer.render(this.quadScene, this.camera);
    this.renderer.setRenderTarget(null);
    this.dirty.delete(lighting);
    this.options.onEvent?.({ type: 'rendered', ms: performance.now() - started });
  }

  protected requestFrame(): void {
    if (this.frameRequested || this.disposed) return;
    this.frameRequested = true;
    requestAnimationFrame(this.frame);
  }

  protected frame = (): void => {
    this.frameRequested = false;
    // while the context is lost nothing can be drawn; the restore handler redraws everything
    if (this.disposed || !this.tier || this.contextLost) return;
    const now = performance.now();
    let animating = false;
    if (this.mix < 1) {
      const t = Math.min(1, (now - this.mixStart) / this.mixDuration);
      this.mix = t * t * (3 - 2 * t);
      animating = t < 1;
    }
    const current = this.lighting;
    const from = this.fromLighting;
    if (this.dirty.has(current)) this.renderLighting(current);
    if (this.mix < 1 && from !== current && this.dirty.has(from)) this.renderLighting(from);

    const targetA = this.targets.get(this.mix < 1 ? from : current);
    const targetB = this.targets.get(current);
    if (!targetA || !targetB || !this.assets.has(current)) {
      if (animating) this.requestFrame();
      return;
    }
    const u = this.displayMaterial.uniforms as Record<string, THREE.IUniform>;
    u.uA!.value = targetA.texture;
    u.uB!.value = targetB.texture;
    u.uMix!.value = this.mix < 1 ? this.mix : 0;
    (u.uView!.value as THREE.Vector4).set(this.view.x0, this.view.y0, this.view.x1, this.view.y1);
    const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    (u.uCanvas!.value as THREE.Vector2).copy(size);
    (u.uScene!.value as THREE.Vector2).set(this.tier.width, this.tier.height);
    this.quad.material = this.displayMaterial;
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
    this.renderer.render(this.quadScene, this.camera);
    if (animating) this.requestFrame();
  };

  /** Releases everything that belongs to the displayed tier (passes, targets, surface map). */
  protected releaseTier(): void {
    for (const assets of this.assets.values()) releaseLighting(assets);
    this.assets.clear();
    this.assetLoads.clear();
    for (const rt of this.targets.values()) rt.dispose();
    this.targets.clear();
    this.dirty.clear();
    if (this.surfaceTexture) releaseTexture(this.surfaceTexture);
    this.surfaceTexture = null;
    this.surfacePixels = null;
  }

  protected releaseScene(): void {
    this.releaseTier();
    this.surfaceData?.dispose();
    this.surfaceData = null;
  }

  /**
   * Replaces every GPU-backed object after a context restore. three.js has re-initialised its
   * state, so the old objects refer to GL resources that no longer exist; they are dropped
   * (not disposed, which would try to delete those resources) and replaced by copies that share
   * the decoded images, so nothing is downloaded again. Returns old → new textures.
   */
  protected recreateGpuResources(): Map<THREE.Texture, THREE.Texture> {
    const remap = new Map<THREE.Texture, THREE.Texture>();
    const renew = <T extends THREE.Texture>(texture: T): T => {
      const copy = texture.clone() as T;
      remap.set(texture, copy);
      return copy;
    };
    this.placeholder = renew(this.placeholder);
    for (const [lighting, a] of this.assets) {
      const assets: LightingAssets = {
        bg: renew(a.bg),
        diffuse: renew(a.diffuse),
        gloss: renew(a.gloss),
        satin: renew(a.satin),
        scale: renew(a.scale),
        exposure: a.exposure,
      };
      this.assets.set(lighting, assets);
      this.assetLoads.set(lighting, Promise.resolve(assets));
    }
    if (this.surfaceTexture) this.surfaceTexture = renew(this.surfaceTexture);
    if (this.surfaceData) this.surfaceData = renew(this.surfaceData);
    for (const [from, to] of this.textures.renew()) remap.set(from, to);
    for (const [zone, texture] of this.boundTextures) {
      if (texture) this.boundTextures.set(zone, remap.get(texture) ?? renew(texture));
    }
    this.targets.clear();
    this.sceneMaterial = this.createSceneMaterial();
    this.displayMaterial = this.createDisplayMaterial();
    this.quad.geometry = new THREE.PlaneGeometry(2, 2);
    this.quad.material = this.sceneMaterial;
    return remap;
  }

  private watchPixelRatio(): void {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia(`(resolution: ${globalThis.devicePixelRatio || 1}dppx)`);
    const onChange = () => {
      if (this.disposed) return;
      this.watchPixelRatio();
      this.resize();
    };
    query.addEventListener('change', onChange, { once: true });
    this.stopWatchingPixelRatio = () => query.removeEventListener('change', onChange);
  }

  private onContextLost = (event: Event): void => {
    event.preventDefault();
    this.contextLost = true;
    this.options.onEvent?.({ type: 'error', error: new ContextLostError() });
  };

  private onContextRestored = (): void => {
    if (this.disposed) return;
    this.contextLost = false;
    this.recreateGpuResources();
    this.markAllDirty();
    this.requestFrame();
    this.options.onEvent?.({ type: 'restored' });
    this.checkTier();
  };
}
